// src/components/auth/AuthModal.tsx
import React, { useCallback, useEffect, useRef } from 'react';
import { useAuthStore, type AuthTab } from '../../stores/useAuthStore';
import { LoginForm } from './LoginForm.tsx';
import { SignupForm } from './SignupForm.tsx';

const C = {
  surface: '#0d1220',
  border:  'rgba(255,255,255,0.08)',
  accent:  '#4f8cf7',
  accent2: '#6366f1',
  text:    '#f1f5f9',
  muted:   '#8b9cc4',
};

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'textarea:not([disabled])',
  'input:not([disabled])', 'select:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null
  );
}

const tabStyle = (active: boolean): React.CSSProperties => ({
  flex:            1,
  padding:         '10px 0',
  borderRadius:    '10px',
  border:          'none',
  cursor:          'pointer',
  fontSize:        '0.78rem',
  fontWeight:      700,
  letterSpacing:   '0.07em',
  textTransform:   'uppercase',
  background:      active
    ? `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 100%)`
    : 'transparent',
  color:           active ? '#fff' : C.muted,
  transition:      'all 0.2s ease',
  boxShadow:       active ? '0 4px 12px rgba(79,140,247,0.3)' : 'none',
});

export function AuthModal(): React.ReactElement | null {
  const open       = useAuthStore((s) => s.authModalOpen);
  const tab        = useAuthStore((s) => s.authModalTab);
  const setTab     = useAuthStore((s) => s.setAuthTab);
  const closeModal = useAuthStore((s) => s.closeAuthModal);

  const modalRef = useRef<HTMLDivElement>(null);
  const [closeBtnHover, setCloseBtnHover] = React.useState(false);

  useEffect(() => {
    if (!open || !modalRef.current) return;
    const modal    = modalRef.current;
    const focusable = getFocusable(modal);
    focusable[0]?.focus();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { closeModal(); return; }
      if (e.key !== 'Tab') return;
      const els = getFocusable(modal);
      if (!els.length) { e.preventDefault(); return; }
      const [first, last] = [els[0], els[els.length - 1]];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    };
    modal.addEventListener('keydown', handleKeyDown);
    return () => modal.removeEventListener('keydown', handleKeyDown);
  }, [open, closeModal, tab]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const switchTab = useCallback((t: AuthTab) => { setTab(t); }, [setTab]);

  if (!open) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) closeModal();
  };

  return (
    <div
      style={{
        position:      'fixed',
        inset:         0,
        background:    'rgba(0,0,0,0.75)',
        backdropFilter:'blur(12px)',
        display:       'flex',
        alignItems:    'center',
        justifyContent:'center',
        zIndex:        1000,
        padding:       '20px',
      }}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={tab === 'login' ? 'Sign in' : tab === 'signup' ? 'Create account' : 'Reset password'}
    >
      <div
        ref={modalRef}
        style={{
          background:    'rgba(13,18,32,0.98)',
          backdropFilter:'blur(40px)',
          border:        '1px solid rgba(255,255,255,0.08)',
          borderRadius:  '24px',
          width:         '100%',
          maxWidth:      '460px',
          maxHeight:     '90vh',
          overflowY:     'auto',
          position:      'relative',
          boxShadow:     '0 32px 80px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.08)',
        }}
      >
        {/* Close */}
        <button
          style={{
            position:       'absolute',
            top:            '20px',
            right:          '20px',
            background:     closeBtnHover ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.04)',
            border:         '1px solid rgba(255,255,255,0.08)',
            borderRadius:   '8px',
            cursor:         'pointer',
            color:          closeBtnHover ? C.text : C.muted,
            fontSize:       '16px',
            lineHeight:     1,
            padding:        '6px 8px',
            zIndex:         1,
            transition:     'all 0.15s ease',
            fontFamily:     'inherit',
          }}
          onClick={closeModal}
          aria-label="Close"
          onMouseEnter={() => setCloseBtnHover(true)}
          onMouseLeave={() => setCloseBtnHover(false)}
        >
          ✕
        </button>

        {/* Header */}
        <div style={{ padding: '36px 36px 0' }}>
          <div style={{
            fontSize:      '20px',
            fontWeight:    800,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            marginBottom:  '28px',
            display:       'flex',
            alignItems:    'center',
            gap:           '8px',
          }}>
            <span style={{
              background:           `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor:  'transparent',
              backgroundClip:       'text',
            }}>
              ESP
            </span>
            <span style={{ color: C.text }}>for Sports</span>
          </div>

          {tab !== 'forgot' && (
            <div style={{
              display:        'flex',
              gap:            '4px',
              background:     'rgba(255,255,255,0.04)',
              borderRadius:   '14px',
              padding:        '4px',
              border:         '1px solid rgba(255,255,255,0.06)',
            }} role="tablist">
              {(['login', 'signup'] as const).map((t) => (
                <button
                  key={t}
                  role="tab"
                  aria-selected={tab === t}
                  style={tabStyle(tab === t)}
                  onClick={() => switchTab(t)}
                >
                  {t === 'login' ? 'Sign In' : 'Sign Up'}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: '28px 36px 36px' }}>
          {tab === 'login'  && <LoginForm />}
          {tab === 'signup' && <SignupForm />}
          {tab === 'forgot' && <div style={{ color: C.muted, textAlign: 'center', padding: '20px 0', fontSize: '0.9rem' }}>Reset form coming soon…</div>}
        </div>
      </div>
    </div>
  );
}
